import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';

const AgentChat = ({ workflowId, onClose, onMinimize, onChatStart, token, isPublic, publicToken, variant }) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const [files, setFiles] = useState([]);
    const [sessionId] = useState(() => crypto.randomUUID());
    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFiles = Array.from(e.target.files);
            
            const filePromises = newFiles.map(file => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        resolve({
                            name: file.name,
                            type: file.type,
                            size: file.size,
                            content: event.target.result.split(',')[1] // Extract base64 part
                        });
                    };
                    reader.onerror = (error) => reject(error);
                    reader.readAsDataURL(file);
                });
            });

            Promise.all(filePromises).then(processedFiles => {
                setFiles(prev => [...prev, ...processedFiles]);
            }).catch(error => {
                console.error("Error reading files", error);
            });
        }
        // Reset the input value to allow selecting the same file again
        e.target.value = '';
    };

    const removeFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async () => {
        if ((!input.trim() && files.length === 0) || loading) return;
        
        if (onChatStart) onChatStart();

        const currentFiles = [...files];
        const userMessage = { 
            role: 'user', 
            content: input,
            files: currentFiles.map(f => f.name)
        };

        setMessages(prev => [...prev, userMessage]);
        setInput('');
        setFiles([]);
        setLoading(true);

        try {
            if (isPublic && !publicToken) {
                throw new Error("Public chat token missing");
            }

            const payload = {
                message: userMessage.content,
                session_id: sessionId,
                files: currentFiles
            };

            const endpoint = isPublic
                ? `${API_BASE_URL}/public/chat/${publicToken}`
                : `${API_BASE_URL}/workflows/${workflowId}/chat`;

            const headers = isPublic ? {} : { Authorization: `Bearer ${token}` };

            const response = await axios.post(
                endpoint,
                payload,
                { headers }
            );

            const agentMessage = { role: 'agent', content: response.data.response };
            setMessages(prev => [...prev, agentMessage]);
        } catch (error) {
            console.error(error);
            const errorMessage = { 
                role: 'error', 
                content: "Error: " + (error.response?.data?.detail || error.message) 
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const isPage = variant === 'page';
    const containerStyle = {
        position: isPage ? 'relative' : 'fixed',
        bottom: isPage ? '0' : '20px',
        right: isPage ? '0' : '20px',
        width: isPage ? '100%' : '400px',
        height: isPage ? '100%' : '600px',
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: isPage ? '0' : '12px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: isPage ? 'none' : '0 4px 20px rgba(0,0,0,0.3)',
        zIndex: 1000,
        overflow: 'hidden'
    };

    return (
        <div style={{
            ...containerStyle
        }}>
            <div style={{
                padding: '15px',
                borderBottom: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                backgroundColor: 'var(--bg-primary)'
            }}>
                <div style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>🤖 Agent Chat</div>
                {(onMinimize || onClose) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {onMinimize && (
                            <button 
                                onClick={onMinimize}
                                title="Minimize (Keep Session)"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    fontSize: '18px',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px'
                                }}
                            >
                                ─
                            </button>
                        )}
                        {onClose && (
                            <button 
                                onClick={onClose}
                                title="Close (End Session)"
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'var(--text-secondary)',
                                    cursor: 'pointer',
                                    fontSize: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '24px',
                                    height: '24px'
                                }}
                            >
                                &times;
                            </button>
                        )}
                    </div>
                )}
            </div>

            <div style={{
                flex: 1,
                padding: '15px',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
            }}>
                {messages.length === 0 && (
                    <div style={{ color: 'var(--text-tertiary)', textAlign: 'center', marginTop: '20px' }}>
                        Start a conversation with the agent...
                    </div>
                )}
                {messages.map((msg, index) => (
                    <div key={index} style={{
                        alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: '80%',
                        backgroundColor: msg.role === 'user' ? 'var(--primary-color)' : 
                                       msg.role === 'error' ? 'var(--error-color)' : 'var(--bg-node)',
                        color: msg.role === 'user' || msg.role === 'error' ? 'white' : 'var(--text-primary)',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        borderBottomRightRadius: msg.role === 'user' ? '2px' : '12px',
                        borderBottomLeftRadius: msg.role !== 'user' ? '2px' : '12px',
                        fontSize: '14px',
                        whiteSpace: 'pre-wrap',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}>
                        {msg.content}
                        {msg.files && msg.files.length > 0 && (
                            <div style={{ marginTop: '8px', fontSize: '12px', opacity: 0.8 }}>
                                <strong>Attachments:</strong>
                                <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                                    {msg.files.map((f, i) => <li key={i}>{f}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                ))}
                {loading && (
                    <div style={{ alignSelf: 'flex-start', color: 'var(--text-tertiary)', fontSize: '12px', marginLeft: '10px' }}>
                        Agent is thinking...
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            <div style={{
                padding: '15px',
                borderTop: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-primary)',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px'
            }}>
                {files.length > 0 && (
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {files.map((file, index) => (
                            <div key={index} style={{
                                fontSize: '12px',
                                padding: '4px 8px',
                                background: 'var(--bg-tertiary)',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                color: 'var(--text-primary)'
                            }}>
                                <span>📄 {file.name}</span>
                                <button 
                                    onClick={() => removeFile(index)}
                                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-secondary)' }}
                                >
                                    &times;
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div style={{ display: 'flex', gap: '10px' }}>
                    <input
                        type="file"
                        multiple
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileChange}
                    />
                    <button
                        onClick={() => {
                            if (fileInputRef.current) {
                                fileInputRef.current.value = '';
                                fileInputRef.current.click();
                            }
                        }}
                        style={{
                            background: 'transparent',
                            border: '1px solid var(--border-color)',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            padding: '0 10px',
                            fontSize: '20px',
                            color: 'var(--text-primary)'
                        }}
                        title="Attach files"
                    >
                        📎
                    </button>
                    <textarea
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type a message..."
                        style={{
                            flex: 1,
                            padding: '10px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)',
                            backgroundColor: 'var(--bg-input)',
                            color: 'var(--text-primary)',
                            resize: 'none',
                            height: '40px',
                            fontFamily: 'inherit'
                        }}
                    />
                    <button
                        onClick={handleSend}
                        disabled={loading || (!input.trim() && files.length === 0)}
                        style={{
                            padding: '0 20px',
                            borderRadius: '8px',
                            border: 'none',
                            backgroundColor: 'var(--primary-color)',
                            color: 'white',
                            cursor: loading || (!input.trim() && files.length === 0) ? 'not-allowed' : 'pointer',
                            opacity: loading || (!input.trim() && files.length === 0) ? 0.7 : 1
                        }}
                    >
                        Send
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AgentChat;
